import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Box, Chip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import { EditIcon, TrashIcon } from '../components/Icons';
import { ERole } from '../enums/user/ERole';
import { ETheme } from '../enums/user/ETheme';
import { getProjectStatusColor } from '../functions/getChipColors';
import { IProject } from '../interfaces/projects/IProject';
import { darkTheme, lightTheme } from '../mui/theme';

export const useProjectColumns = (
  setSelectedProject: (project: IProject) => void,
  setShowDeleteModal: (show: boolean) => void,
  hideActionColumn = false,
): GridColDef[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'name',
      headerName: t('projects.name'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'client',
      headerName: t('general.client'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => params?.row?.client?.name ?? '-',
    },
    {
      field: 'users',
      headerName: t('projects.numOfEmployees'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => params?.value?.length,
    },
    {
      field: 'status',
      headerName: t('general.status'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`projects.status.${params.value}`)} // Translated status
          color={getProjectStatusColor(params.value)}
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 150,
      sortable: false,
      headerClassName: hideActionColumn ? 'hideColumn' : '',
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Box
          sx={{
            display: hideActionColumn ? 'none' : 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {/* Edit or View Icon */}
          <span
            style={{ cursor: 'pointer', display: 'flex' }}
            onClick={() =>
              navigate(
                `/${isNormalUser ? 'user' : 'admin'}/projects/${params.row?.id}`,
              )
            }
          >
            {!isNormalUser ? (
              <EditIcon
                fill={
                  userData?.theme === ETheme.LIGHT
                    ? lightTheme.palette.text.primary
                    : darkTheme.palette.text.primary
                }
              />
            ) : (
              <RemoveRedEyeOutlinedIcon
                fill={
                  userData?.theme === ETheme.LIGHT
                    ? lightTheme.palette.text.primary
                    : darkTheme.palette.text.primary
                }
              />
            )}
          </span>

          {/* Delete Icon */}
          {!isNormalUser && (
            <span
              style={{ cursor: 'pointer' }}
              className={'ms-3 d-flex'}
              onClick={() => {
                setSelectedProject(params.row);
                setShowDeleteModal(true);
              }}
            >
              <TrashIcon
                fill={
                  userData?.theme === ETheme.LIGHT
                    ? lightTheme.palette.text.primary
                    : darkTheme.palette.text.primary
                }
              />
            </span>
          )}
        </Box>
      ),
    },
  ];
};
